/**
 * ChatIntake — /valuation/chat
 *
 * Conversational front-end for Task 3's anonymous intake API. Centerpiece of
 * the Plan-05 conversational-intake feature: a chat with the interview
 * agent that ends either at a "ready to confirm" hand-off to Task 6's
 * confirm screen, or (message/token cap, or LLM outage) a hand-off to the
 * existing LiteWizard form — the conversation's progress is never lost,
 * it's translated into the wizard's own draft shape and carried over.
 *
 * Session lifecycle:
 *   - On mount: reuse `sessionStorage['intake_id']` via GET /intake/:id if
 *     present (so a page refresh resumes the same conversation instead of
 *     starting over); otherwise POST /intake to create one and seed the
 *     greeting.
 *   - Each user turn: POST /intake/:id/messages. A 503 means the LLM is
 *     down — show generic retry copy inline and re-enable the input; the
 *     persistent footer link is always available as an escape hatch.
 *   - `capped: true` (message/token budget exhausted) ends the session
 *     server-side; the UI fetches the latest partialInput and writes it to
 *     the wizard's draft key, then offers a "Continue with the form" CTA.
 *   - `readyToConfirm: true` offers a CTA to Task 6's /valuation/confirm
 *     screen, passed `{ intakeId }` via router state.
 *
 * Spec: docs/superpowers/specs/2026-08-10-conversational-intake-design.md
 * Plan: .superpowers/sdd/2026-08-10-conversational-intake/task-5-brief.md
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createIntake, sendIntakeMessage, getIntake } from '../services/api';
import { createEmptyFormData, RISK_QUESTIONS } from '../components/lite/liteQuestions';
import './ChatIntake.css';

const INTAKE_ID_KEY = 'intake_id';
const DRAFT_KEY = 'lite_wizard_draft';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again, or use the form instead.';
const TOTAL_RISK_QUESTIONS = RISK_QUESTIONS.length;
const MAX_MESSAGE_LENGTH = 2000;
const PRIVACY_LINE =
  'This conversation is powered by an AI assistant; what you type is processed by our AI provider to prepare your valuation.';

/**
 * isReadyToConfirm — the CTA to /valuation/confirm must never appear on the
 * model's say-so alone. The model's own `readyToConfirm` flag can be wrong
 * (especially on smaller/open-weight models) and prematurely surface the
 * CTA; the session's actual, deterministic readiness is `progress.ready`
 * (computed server-side from partialInput + collectedPaths, same value that
 * drives the persisted session `status`). Require both so a false-positive
 * model flag can never send someone into a confirm screen that 409s.
 */
function isReadyToConfirm(modelFlag, progress) {
  return Boolean(modelFlag) && Boolean(progress && progress.ready);
}

// Required-value paths (see backend/services/intakeAgent.js completeness())
// that belong to the "financials" topic vs. the "balance sheet" topic, for
// the progress line's two ✓/… markers. Deliberately mirrors the backend's
// REQUIRED_VALUE_PATHS + REQUIRED_COLLECTED_PATHS grouping, not re-derived
// from anything the frontend imports (no shared module between the two
// apps), so if the backend's required-path list ever changes this grouping
// needs a matching update.
const FINANCIALS_TOPIC_PATHS = [
  'company.industry',
  'financials.lastFiscalYear',
  'financials.historicalEBITDA.yearAnchor',
  'financials.historicalSDE.yearAnchor',
  'financials.dealStructure',
  'ruleOfThumb.multiple',
];

const BALANCE_SHEET_TOPIC_PATHS = [
  'financials.balanceSheet.summary.totalAssets',
  'financials.balanceSheet.summary.totalLiabilities',
];

/**
 * partialInputToWizardDraft — maps a Task 3 partialInput (MVAInput-shaped,
 * `lite` variant) onto EXACTLY the shape LiteWizard's `formData` uses (see
 * liteQuestions.createEmptyFormData and LiteWizard.js DRAFT_KEY / the
 * `sanitizeDraft` load path). Starting from createEmptyFormData() as the
 * base and only overwriting fields the conversation actually touched means
 * anything the user never got to keeps the wizard's own default instead of
 * silently disappearing.
 *
 * Load-bearing gotcha: LiteWizard stores its numeric-looking fields
 * (ebitdaAnchor, totalAssets, foundingYear, ...) as STRINGS in formData —
 * they come straight off <input> DOM values — and LiteWizard.sanitizeDraft()
 * only keeps a key when `typeof raw[key] === typeof emptyFormData[key]`.
 * Writing a real number there would silently fail that typeof check on
 * load and the field would revert to ''. Every non-risk numeric field is
 * therefore stringified here. riskAnswers is the one exception:
 * sanitizeDraft validates those individually as integers 0..4, not by
 * typeof against the empty shape, so those stay real numbers (or null).
 */
export function partialInputToWizardDraft(partialInput) {
  const draft = createEmptyFormData();
  if (!partialInput || typeof partialInput !== 'object') return draft;

  const toStr = (v) => (v === null || v === undefined ? '' : String(v));

  const company = partialInput.company || {};
  const financials = partialInput.financials || {};
  const ebitda = financials.historicalEBITDA || {};
  const sde = financials.historicalSDE || {};
  const balanceSheet = (financials.balanceSheet && financials.balanceSheet.summary) || {};
  const risk = partialInput.risk || {};
  const liteAnswers = risk.liteAnswers || {};
  const ruleOfThumb = partialInput.ruleOfThumb || {};

  draft.companyName = company.name || '';
  draft.industry = company.industry || '';
  draft.foundingYear = toStr(company.foundingYear);
  draft.employeeCount = toStr(company.employeeCount);
  draft.location = company.location || '';
  draft.canadianCompany = financials.dealStructure === 'YES';

  draft.lastFiscalYear = toStr(financials.lastFiscalYear);
  draft.ebitdaAnchor = toStr(ebitda.yearAnchor);
  draft.ebitdaMinus1 = toStr(ebitda.yearMinus1);
  draft.ebitdaMinus2 = toStr(ebitda.yearMinus2);
  draft.ebitdaMinus3 = toStr(ebitda.yearMinus3);
  draft.ebitdaMinus4 = toStr(ebitda.yearMinus4);
  draft.sdeAnchor = toStr(sde.yearAnchor);
  draft.sdeMinus1 = toStr(sde.yearMinus1);
  draft.sdeMinus2 = toStr(sde.yearMinus2);
  draft.sdeMinus3 = toStr(sde.yearMinus3);
  draft.sdeMinus4 = toStr(sde.yearMinus4);

  draft.totalAssets = toStr(balanceSheet.totalAssets);
  draft.totalLiabilities = toStr(balanceSheet.totalLiabilities);
  draft.inventory = toStr(balanceSheet.inventory);

  RISK_QUESTIONS.forEach(({ key }) => {
    const v = liteAnswers[key];
    draft.riskAnswers[key] = Number.isInteger(v) ? v : null;
  });

  if (typeof ruleOfThumb.multiple === 'number' && Number.isFinite(ruleOfThumb.multiple)) {
    draft.ruleOfThumbMultiple = String(ruleOfThumb.multiple);
  }

  // draft.addInventory is never asked about in conversation — left at the
  // wizard's own default (false) from createEmptyFormData() above.

  return draft;
}

function buildProgressLine(progress) {
  if (!progress) return null;
  const missing = new Set(progress.requiredMissing || []);
  const financialsDone = FINANCIALS_TOPIC_PATHS.every((p) => !missing.has(p));
  const balanceSheetDone = BALANCE_SHEET_TOPIC_PATHS.every((p) => !missing.has(p));
  const mark = (done) => (done ? '✓' : '…');
  return (
    `Topics covered: financials ${mark(financialsDone)} · ` +
    `balance sheet ${mark(balanceSheetDone)} · ` +
    `risk ${progress.riskAnswered ?? 0}/${TOTAL_RISK_QUESTIONS}`
  );
}

function writeWizardDraft(partialInput) {
  try {
    const draft = partialInputToWizardDraft(partialInput);
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (err) {
    // Best-effort — localStorage can throw (private browsing, quota
    // exceeded, disabled). Never block the hand-off navigation on it; the
    // user just lands on a blank form instead of a pre-filled one.
    console.error('Could not save conversation progress to the form draft:', err);
  }
}

function ChatIntake() {
  const navigate = useNavigate();
  const [intakeId, setIntakeId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [progress, setProgress] = useState(null);
  const [capped, setCapped] = useState(false);
  const [readyToConfirm, setReadyToConfirm] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState(null);
  const [turnError, setTurnError] = useState(null);
  const listRef = useRef(null);
  // Guards against creating two sessions per mount. React.StrictMode
  // (frontend/src/index.js) double-invokes effects in dev — mount, cleanup,
  // mount again, synchronously, before any awaited call resolves — and
  // without this guard both invocations would read sessionStorage before
  // either had written the new id, so both would fall into the
  // `createIntake()` path and POST — orphaning a session and burning the
  // IP's daily cap at twice the intended rate. The underlying unguarded-init
  // race isn't StrictMode-specific: any remount or double-render path has
  // the same failure mode.
  //
  // initRef ensures createIntake()/getIntake() is only kicked off once, on
  // the first effect invocation. cancelledRef tracks whether the component
  // is currently "live" for the purpose of applying that in-flight call's
  // result: it's reset to false at the *start* of every effect invocation
  // (including the guarded no-op ones) and set to true by every cleanup.
  // That reset is what makes this safe under StrictMode: the phantom
  // cleanup between the two synchronous mounts flips cancelledRef to true,
  // but the second (real) mount immediately flips it back to false before
  // the in-flight init() promise resolves, so the result is still applied.
  // On a genuine unmount, no second effect invocation follows to reset it,
  // so the stale response is correctly discarded.
  const initRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    if (!initRef.current) {
      initRef.current = true;

      (async function init() {
        const existingId = window.sessionStorage.getItem(INTAKE_ID_KEY);
        try {
          if (existingId) {
            const state = await getIntake(existingId);
            if (cancelledRef.current) return;
            setIntakeId(existingId);
            setTranscript(state.transcript || []);
            setProgress(state.progress || null);
            setCapped(state.status === 'capped');
            setReadyToConfirm(isReadyToConfirm(state.status === 'ready', state.progress));
          } else {
            const created = await createIntake();
            if (cancelledRef.current) return;
            window.sessionStorage.setItem(INTAKE_ID_KEY, created.intakeId);
            setIntakeId(created.intakeId);
            setTranscript([{ role: 'assistant', content: created.greeting }]);
          }
        } catch (err) {
          if (cancelledRef.current) return;
          console.error('Could not start the conversation:', err);
          setInitError(
            (err.response && err.response.data && err.response.data.error) ||
              'Could not start a conversation. Please use the form instead.'
          );
        } finally {
          if (!cancelledRef.current) setInitializing(false);
        }
      })();
    }

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [transcript, pending]);

  const handleSwitchToForm = useCallback(async () => {
    if (intakeId) {
      try {
        const state = await getIntake(intakeId);
        writeWizardDraft(state.partialInput);
      } catch (err) {
        console.error('Could not fetch conversation progress before switching to the form:', err);
      }
    }
    navigate('/valuation/form');
  }, [intakeId, navigate]);

  const handleReview = useCallback(() => {
    navigate('/valuation/confirm', { state: { intakeId } });
  }, [intakeId, navigate]);

  const handleSend = async (e) => {
    e.preventDefault();
    const message = input.trim();
    if (!message || pending || !intakeId || capped) return;

    setTranscript((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    setPending(true);
    setTurnError(null);

    try {
      const result = await sendIntakeMessage(intakeId, message);
      setTranscript((prev) => [...prev, { role: 'assistant', content: result.assistantMessage }]);
      setProgress(result.progress);
      setReadyToConfirm(isReadyToConfirm(result.readyToConfirm, result.progress));
      setCapped(Boolean(result.capped));
    } catch (err) {
      const serverMessage = err.response && err.response.data && err.response.data.error;
      setTurnError(serverMessage || GENERIC_ERROR_MESSAGE);
      // The turn never persisted server-side (it errored before or during the
      // LLM call), so the optimistic user bubble we appended above is now a
      // lie — drop it and give the typed text back to the input so the owner
      // can retry without retyping it from scratch.
      setTranscript((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'user' && last.content === message) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      setInput(message);
    } finally {
      setPending(false);
    }
  };

  const progressLine = buildProgressLine(progress);

  if (initializing) {
    return (
      <div className="chat-intake-page">
        <header className="chat-intake-brandbar">
          <span className="chat-intake-wordmark">Exit Ready</span>
        </header>
        <main className="chat-intake-main">
          <p className="chat-intake-loading" role="status">
            Loading&hellip;
          </p>
        </main>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="chat-intake-page">
        <header className="chat-intake-brandbar">
          <span className="chat-intake-wordmark">Exit Ready</span>
        </header>
        <main className="chat-intake-main">
          <p className="chat-intake-error-banner" role="alert">
            {initError}
          </p>
          <button
            type="button"
            className="chat-intake-btn chat-intake-btn--primary"
            onClick={handleSwitchToForm}
          >
            Continue with the form
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="chat-intake-page">
      <header className="chat-intake-brandbar">
        <span className="chat-intake-wordmark">Exit Ready</span>
      </header>

      <main className="chat-intake-main">
        {progressLine && <p className="chat-intake-progress">{progressLine}</p>}

        <div
          className="chat-intake-messages"
          role="log"
          aria-live="polite"
          aria-label="Conversation with the valuation assistant"
          ref={listRef}
        >
          {transcript.map((msg, idx) => (
            <div
              key={idx}
              className={
                'chat-intake-message ' +
                (msg.role === 'user' ? 'chat-intake-message--user' : 'chat-intake-message--assistant')
              }
            >
              {msg.content}
            </div>
          ))}
          {pending && (
            <div
              className="chat-intake-message chat-intake-message--assistant chat-intake-typing"
              aria-label="Assistant is typing"
            >
              ●●●
            </div>
          )}
        </div>

        {turnError && (
          <div className="chat-intake-error-banner" role="alert">
            <p>{turnError}</p>
          </div>
        )}

        {capped && (
          <div className="chat-intake-handoff" role="status">
            <p>
              We&rsquo;ve covered a lot of ground. Let&rsquo;s finish this up on the quick form
              instead — everything we discussed carries over.
            </p>
            <button
              type="button"
              className="chat-intake-btn chat-intake-btn--primary"
              onClick={handleSwitchToForm}
            >
              Continue with the form
            </button>
          </div>
        )}

        {readyToConfirm && !capped && (
          <div className="chat-intake-ready" role="status">
            <p>Great, I have everything &mdash; let&rsquo;s review it together.</p>
            <button
              type="button"
              className="chat-intake-btn chat-intake-btn--primary"
              onClick={handleReview}
            >
              Review my answers
            </button>
          </div>
        )}

        <form className="chat-intake-form" onSubmit={handleSend}>
          <label htmlFor="chat-intake-input" className="chat-intake-input-label">
            Your message
          </label>
          <div className="chat-intake-input-row">
            <input
              id="chat-intake-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={pending || capped}
              placeholder="Type your answer&hellip;"
              autoComplete="off"
              maxLength={MAX_MESSAGE_LENGTH}
            />
            <button
              type="submit"
              className="chat-intake-btn chat-intake-btn--primary"
              disabled={pending || capped || !input.trim()}
            >
              Send
            </button>
          </div>
        </form>

        <footer className="chat-intake-footer">
          <button type="button" className="chat-intake-footer-link" onClick={handleSwitchToForm}>
            Prefer a form? Switch anytime — your answers carry over
          </button>
          <p className="chat-intake-privacy">{PRIVACY_LINE}</p>
        </footer>
      </main>
    </div>
  );
}

export default ChatIntake;
